import type { TFunction } from "i18next";
import { useMemo } from "react";
import { isStaffingModuleEnabled } from "model/settings.logic";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  isFreeSubscription,
  isSubscriptionLoadedForCompany,
} from "web-apps/company/utils/subscription.logic";

export type MenuItem = {
  readonly text: string;
  readonly path: string;
  readonly image: string;
  readonly disabledReason?: string;
};

export const useMenuList = (
  userType: string | null,
  t: TFunction,
): MenuItem[] => {
  const [companyState] = useCompanyStore();
  const isFree = isFreeSubscription(companyState.subscription.info);
  const isSubscriptionRestricted =
    companyState.company?.id != null &&
    (!isSubscriptionLoadedForCompany({
      companyId: companyState.company.id,
      isLoading: companyState.subscription.isLoading,
      subscription: companyState.subscription.info,
    }) ||
      isFree);
  const disabledReason = isSubscriptionRestricted
    ? t("Subscription.UpgradeToAccess")
    : undefined;

  const hasStaffingModule = useMemo(
    () => isStaffingModuleEnabled(companyState.companySettings),
    [companyState.companySettings],
  );

  return useMemo(() => {
    switch (userType) {
      case "Company":
        return [
          {
            text: t("Schedule.ScheduleTitle"),
            path: "/company/schedule",
            image: "calendar",
          },
          {
            text: t("GigsCompany.Gigs"),
            path: "/company/gigs",
            image: "document",
            disabledReason,
          },

          ...(hasStaffingModule
            ? [
                {
                  text: t("ApplicationsCompany.Recruit"),
                  path: "/company/applications",
                  image: "idbadge",
                  disabledReason,
                },
              ]
            : []),

          {
            text: t("OrganizationalStructure.OrganizationalStructure"),
            path: "/company/settings?page=organization",
            image: "company",
          },
          {
            text: t("Salary.Salary"),
            path: "/company/salary",
            image: "wallet",
          },
          {
            text: t("CompanyTimeReport.TimeReports"),
            path: "/company/time-reports",
            image: "clock",
          },
        ];

      case "Employee":
        return [
          {
            text: t("CompanyEmployee.Schedule"),
            path: "/employee/schedule",
            image: "calendar",
          },
          {
            text: t("CompanyProfile.Profile"),
            path: "/employee/profile",
            image: "person",
          },
        ];

      default:
        return [];
    }
  }, [disabledReason, hasStaffingModule, t, userType]);
};
